import { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const THEME_STORAGE_KEY = 'clinlexis_theme';

/**
 * Forces light mode while the component is mounted.
 * Used on public/auth pages that should always appear in light theme.
 * Restores the previous dark mode state on unmount.
 *
 * Saves the real preference under a separate localStorage key so it
 * survives a page refresh while on a force-light page.
 */
export function useForceLightMode() {
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);

  useEffect(() => {
    // Persist the user's real preference before we override it
    const realPref = previousThemeRef.current;
    if (realPref !== 'light') {
      localStorage.setItem(`${THEME_STORAGE_KEY}_real`, realPref);
    }
    setTheme('light');

    return () => {
      // Restore from ref first, fall back to the saved real preference
      const prev = previousThemeRef.current !== 'light'
        ? previousThemeRef.current
        : localStorage.getItem(`${THEME_STORAGE_KEY}_real`) as 'light' | 'dark' | null;

      if (prev && prev !== 'light') {
        setTheme(prev);
      }
      localStorage.removeItem(`${THEME_STORAGE_KEY}_real`);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
